import React from 'react';
import styled from 'styled-components';

interface StepContentProps {
  step: number;
}

interface BaseStepContent {
  title: string;
  description: string;
  conclusion: string;
}

interface StepWithList extends BaseStepContent {
  steps: string[];
  infoBox?: never;
}

interface StepWithInfoBox extends BaseStepContent {
  infoBox: {
    title: string;
    content: (string | React.ReactNode)[];
  };
  steps?: never;
}

type StepContentType = StepWithList | StepWithInfoBox;

const ContentContainer = styled.div`
  padding: 1.5rem;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--gray-900);
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  color: var(--gray-700);
  margin-bottom: 1.25rem;
  line-height: 1.6;
`;

const List = styled.ol`
  space-y-4;
  color: var(--gray-700);
  margin-bottom: 1.5rem;
`;

const ListItem = styled.li`
  display: flex;
  align-items: start;
  margin-bottom: 1rem;
`;

const NumberBadge = styled.span`
  background: var(--blue-100);
  color: var(--blue-800);
  font-weight: 600;
  border-radius: 9999px;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const InfoBox = styled.div`
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const InfoTitle = styled.h4`
  font-weight: 600;
  color: var(--yellow-800);
  margin-bottom: 0.75rem;
`;

const stepContents: Record<number, StepContentType> = {
  1: {
    title: "From Soybeans to Soy Milk",
    description: "The process begins with high-quality, non-GMO soybeans that are carefully selected for their protein content and flavor profile. These soybeans undergo a multi-step transformation:",
    steps: [
      "Soaking: Dried soybeans are soaked in water for 8-12 hours, allowing them to hydrate and soften.",
      "Grinding: The hydrated beans are ground with water to create a slurry, breaking down cell walls to release proteins.",
      "Filtering: The slurry is filtered to separate the soluble components (soy milk) from the insoluble fiber (okara)."
    ],
    conclusion: "The resulting soy milk contains approximately 3-4% protein, mostly in the form of glycinin and β-conglycinin. These proteins will be crucial in the next steps of the tofu-making process."
  },
  2: {
    title: "Understanding Protein Structure",
    description: "Before coagulation can occur, we need to understand what happens to the proteins in soy milk at a molecular level:",
    infoBox: {
      title: "The Science of Soy Proteins",
      content: [
        "Hydrophobic (water-fearing) amino acids tucked inside",
        "Hydrophilic (water-loving) amino acids on the outside",
        "Negative charges on their surface that repel each other"
      ]
    },
    conclusion: "When soy milk is heated to around 80°C (175°F), the proteins begin to unfold, exposing their hydrophobic interiors. This process, called denaturation, is essential for proper coagulation in the next step."
  },
  3: {
    title: "The Chemistry of Coagulation",
    description: "Coagulation is where science meets craftmanship in tofu making. This is the critical moment when liquid soy milk transforms into solid curds:",
    infoBox: {
      title: "How Coagulation Works",
      content: [
        "The coagulant introduces positively charged ions (Mg²⁺ or Ca²⁺)",
        "These ions neutralize the negative charges on protein surfaces",
        "Without repelling charges, proteins can approach each other",
        "Exposed hydrophobic regions bond, forming a three-dimensional network",
        "Water is trapped within this network, creating a gel-like structure"
      ]
    },
    conclusion: "The type of coagulant used dramatically affects this process. At Ota Tofu, we use nigari (magnesium chloride) which creates a delicate network structure that yields the perfect balance of firmness and silkiness."
  },
  4: {
    title: "Nigari vs. Calcium Sulfate: The Critical Difference",
    description: "The choice of coagulant is a highly significant decision in tofu production. Calcium Sulfate dominates the industry due to its ease of use in mass production, but at Ota Tofu, we believe that nigari creates a superior product:",
    infoBox: {
      title: "Comparing Coagulants",
      content: [
        <><strong key="nigari">Nigari (MgCl₂):</strong> <span role="img" aria-label="check">✅</span></>,
        "• Creates a delicate, intricate protein network",
        "• Produces tofu with a silkier, more refined texture",
        "• Brings out the natural sweetness of the soybeans",
        "• Requires more skill and careful handling",
        <br key="break1" />,
        <><strong key="calcium">Calcium Sulfate (CaSO₄):</strong> <span role="img" aria-label="x">❌</span></>,
        "• Creates a more rigid, uniform protein structure",
        "• Produces tofu with firmer, more uniform texture",
        "• Adds a subtle mineral or chalky taste",
        "• Easier to work with in mass production"
      ]
    },
    conclusion: "While calcium sulfate (gypsum) is easier to work with in mass production, it creates a more rigid, uniform protein structure that lacks the delicate texture and clean flavor profile of nigari-coagulated tofu."
  },
  5: {
    title: "The Final Texture: Why Nigari Makes a Difference",
    description: "The molecular structure created by nigari coagulation affects not just how the tofu feels in your mouth, but also how it interacts with other ingredients:",
    infoBox: {
      title: "Comparing Final Results",
      content: [
        <><strong key="nigari">Nigari Tofu:</strong> <span role="img" aria-label="check">✅</span></>,
        "• Absorbs flavors more readily",
        "• Better texture retention when cooked",
        "• More delicate mouthfeel",
        "• Cleaner finish on the palate",
        <br key="break2" />,
        <><strong key="gypsum">Gypsum (Calcium Sulfate)Tofu:</strong> <span role="img" aria-label="x">❌</span></>,
        "• More uniform texture",
        "• Less flavor absorption",
        "• Firmer structure in cooking",
        "• Subtle mineral aftertaste"
      ]
    },
    conclusion: "At Ota Tofu, our choice to use nigari reflects our commitment to quality over quantity. The more labor-intensive process required by nigari's delicate curds ensures that each block of our tofu delivers a premium taste and texture experience that machine-processed tofu simply cannot match."
  }
};

export const StepContent: React.FC<StepContentProps> = ({ step }) => {
  const content = stepContents[step];
  
  if (!content) return null;

  return (
    <ContentContainer>
      <StepTitle>{content.title}</StepTitle>
      <StepDescription>{content.description}</StepDescription>
      
      {'steps' in content && content.steps && (
        <List>
          {content.steps.map((stepText: string, index: number) => (
            <ListItem key={index}>
              <NumberBadge>{index + 1}</NumberBadge>
              <span>{stepText}</span>
            </ListItem>
          ))}
        </List>
      )}

      {'infoBox' in content && content.infoBox && (
        <InfoBox>
          <InfoTitle>{content.infoBox.title}</InfoTitle>
          <ul className="pl-6 space-y-2" style={{ listStyle: 'none', paddingLeft: '1.5rem' }}>
            {content.infoBox.content.map((item: any, index: number) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </InfoBox>
      )}

      {content.conclusion && (
        <StepDescription>{content.conclusion}</StepDescription>
      )}
    </ContentContainer>
  );
};

export default StepContent; 